"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import {
  HeadphonesIcon,
  Send,
  Loader2,
  Plus,
  ArrowLeft,
  Image as ImageIcon,
  X,
  MessageSquare,
  Clock,
  CheckCircle,
  XCircle,
  Smile,
  FileImage,
} from "lucide-react";
import toast from "react-hot-toast";
import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  serverTimestamp,
  Timestamp,
  increment,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../firebase";
import EmojiPicker, { Theme } from "emoji-picker-react";
import type { EmojiClickData } from "emoji-picker-react";
import GridBackground from "./GridBackground";

const categories = [
  "General Inquiry",
  "Transfer Issue",
  "Account Problem",
  "Payment Request / Payment Link",
  "Invoice",
  "Personal US Bank Account",
  "Business US Bank Account",
  "Donation Box",
  "Bug Report",
  "Feature Request",
  "KYC / Verification",
  "Bank Account Issue",
  "Security Concern",
  "Billing & Fees",
  "Login / Authentication",
  "Wallet Issue",
  "Other",
];

interface SupportThread {
  id: string;
  userEmail: string;
  userName: string;
  walletAddress: string;
  subject: string;
  category: string;
  status: "open" | "resolved" | "closed";
  lastMessage: string;
  lastMessageAt: any;
  unreadByUser: number;
  unreadByAdmin: number;
  createdAt: any;
}

interface ChatMessage {
  id: string;
  sender: "user" | "admin";
  senderName: string;
  senderEmail: string;
  message: string;
  imageURL?: string;
  createdAt: any;
}

// Animated grid background inspired by reactbits.dev
function AnimatedBackground() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <GridBackground color="97, 206, 112" dotSize={1.2} gap={28} speed={0.003} opacity={0.25} />
    </div>
  );
}

export default function SupportPage() {
  const user = useSelector((state: RootState) => state.user) as any;
  const walletAddress = (user?.smartWallet || user?.wallet || "").toLowerCase();

  const [view, setView] = useState<"threads" | "chat" | "new">("threads");
  const [threads, setThreads] = useState<SupportThread[]>([]);
  const [selectedThread, setSelectedThread] = useState<SupportThread | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [loadingThreads, setLoadingThreads] = useState(true);

  // New thread form
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [category, setCategory] = useState("General Inquiry");
  const [creating, setCreating] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const chatFileInputRef = useRef<HTMLInputElement>(null);
  const [chatImageFile, setChatImageFile] = useState<File | null>(null);
  const [chatImagePreview, setChatImagePreview] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiPickerRef = useRef<HTMLDivElement>(null);

  // Close emoji picker on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(e.target as Node)) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Auto-resize textarea
  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = "auto";
      el.style.height = Math.min(el.scrollHeight, 120) + "px";
    }
  }, []);

  useEffect(() => {
    autoResize();
  }, [newMessage, autoResize]);

  // Load user's threads
  useEffect(() => {
    if (!walletAddress) return;
    const q = query(
      collection(db, "supportThreads"),
      where("walletAddress", "==", walletAddress)
    );
    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs
        .map((d) => ({ id: d.id, ...d.data() } as SupportThread))
        .sort((a, b) => {
          const aTime = a.lastMessageAt?.seconds || 0;
          const bTime = b.lastMessageAt?.seconds || 0;
          return bTime - aTime;
        });
      setThreads(list);
      setLoadingThreads(false);
    }, () => setLoadingThreads(false));
    return () => unsub();
  }, [walletAddress]);

  // Load messages for selected thread
  useEffect(() => {
    if (!selectedThread) { setMessages([]); return; }
    const q = query(
      collection(db, "supportThreads", selectedThread.id, "messages"),
      orderBy("createdAt", "asc")
    );
    const unsub = onSnapshot(q, (snap) => {
      const msgs = snap.docs.map((d) => ({ id: d.id, ...d.data() } as ChatMessage));
      setMessages(msgs);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    });
    if (selectedThread.unreadByUser > 0) {
      updateDoc(doc(db, "supportThreads", selectedThread.id), { unreadByUser: 0 });
    }
    return () => unsub();
  }, [selectedThread]);

  const uploadImage = async (file: File, path: string): Promise<string> => {
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file, { contentType: file.type });
    return await getDownloadURL(storageRef);
  };

  const validateFile = (file: File): boolean => {
    const allowed = ["image/png", "image/jpeg", "image/jpg", "image/gif", "image/webp"];
    if (!allowed.includes(file.type)) {
      toast.error("Only images and GIFs are allowed");
      return false;
    }
    if (file.size > 3 * 1024 * 1024) {
      toast.error("File must be less than 3MB");
      return false;
    }
    return true;
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>, type: "new" | "chat") => {
    const file = e.target.files?.[0];
    if (!file || !validateFile(file)) return;
    const preview = URL.createObjectURL(file);
    if (type === "new") { setImageFile(file); setImagePreview(preview); }
    else { setChatImageFile(file); setChatImagePreview(preview); }
    e.target.value = "";
  };

  const onEmojiClick = (emojiData: EmojiClickData) => {
    setNewMessage((prev) => prev + emojiData.emoji);
    setShowEmojiPicker(false);
    textareaRef.current?.focus();
  };

  const handleCreateThread = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) { toast.error("Please fill in all fields"); return; }
    setCreating(true);
    try {
      let imageURL: string | undefined;
      if (imageFile) {
        const path = `support-images/${walletAddress}/${Date.now()}_${imageFile.name}`;
        imageURL = await uploadImage(imageFile, path);
      }
      const threadRef = await addDoc(collection(db, "supportThreads"), {
        userEmail: user?.email || "",
        userName: user?.username || "CashTap User",
        walletAddress,
        subject, category, status: "open",
        lastMessage: message,
        lastMessageAt: serverTimestamp(),
        unreadByUser: 0, unreadByAdmin: 1,
        createdAt: serverTimestamp(),
      });
      await addDoc(collection(db, "supportThreads", threadRef.id, "messages"), {
        sender: "user",
        senderName: user?.username || "CashTap User",
        senderEmail: user?.email || "",
        message,
        ...(imageURL ? { imageURL } : {}),
        createdAt: serverTimestamp(),
      });
      // Auto-reply from support
      await addDoc(collection(db, "supportThreads", threadRef.id, "messages"), {
        sender: "admin",
        senderName: "CashTap Support",
        senderEmail: "support@cashtap.cash",
        message: "Thank you for reaching out! Our support team has received your request and will get back to you shortly. Response times are typically 12–24 hours. We appreciate your patience!",
        createdAt: serverTimestamp(),
      });
      // Notify admin via email
      fetch(`${import.meta.env.VITE_SERVERURL}/notify-admin-support`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env.VITE_API_SECRET_TOKEN}`,
        },
        body: JSON.stringify({
          username: user?.username || "",
          email: user?.email || "",
          walletAddress,
          subject,
          category,
          message,
          threadId: threadRef.id,
        }),
      }).catch(() => {});
      toast.success("Support thread created!");
      setSubject(""); setMessage(""); setCategory("General Inquiry");
      setImageFile(null); setImagePreview(null);
      setSelectedThread({
        id: threadRef.id, userEmail: user?.email || "", userName: user?.username || "",
        walletAddress, subject, category, status: "open", lastMessage: message,
        lastMessageAt: Timestamp.now(), unreadByUser: 0, unreadByAdmin: 1, createdAt: Timestamp.now(),
      });
      setView("chat");
    } catch (err) {
      console.error("Create thread error:", err);
      toast.error("Failed to create support thread");
    } finally { setCreating(false); }
  };

  const handleSendMessage = async () => {
    if ((!newMessage.trim() && !chatImageFile) || !selectedThread) return;
    setSendingMessage(true);
    try {
      let imageURL: string | undefined;
      if (chatImageFile) {
        const path = `support-images/${walletAddress}/${Date.now()}_${chatImageFile.name}`;
        imageURL = await uploadImage(chatImageFile, path);
      }
      const msgText = newMessage.trim() || (imageURL ? "[Image]" : "");
      await addDoc(collection(db, "supportThreads", selectedThread.id, "messages"), {
        sender: "user",
        senderName: user?.username || "CashTap User",
        senderEmail: user?.email || "",
        message: msgText,
        ...(imageURL ? { imageURL } : {}),
        createdAt: serverTimestamp(),
      });
      await updateDoc(doc(db, "supportThreads", selectedThread.id), {
        lastMessage: msgText,
        lastMessageAt: serverTimestamp(),
        unreadByAdmin: increment(1),
      });
      // Notify admin via email about new user message
      fetch(`${import.meta.env.VITE_SERVERURL}/notify-support-message`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env.VITE_API_SECRET_TOKEN}`,
        },
        body: JSON.stringify({
          senderType: "user",
          senderName: user?.username || "CashTap User",
          recipientEmail: "malikmzl123@gmail.com",
          recipientName: "CashTap Admin",
          subject: selectedThread.subject,
          message: msgText,
          threadId: selectedThread.id,
        }),
      }).catch(() => {});
      setNewMessage(""); setChatImageFile(null); setChatImagePreview(null);
      setShowEmojiPicker(false);
    } catch (err) {
      console.error("Send message error:", err);
      toast.error("Failed to send message");
    } finally { setSendingMessage(false); }
  };

  const formatTime = (ts: any) => {
    if (!ts) return "";
    const date = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    if (diff < 60000) return "Just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 172800000) return "Yesterday";
    return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };

  const formatFullTime = (ts: any) => {
    if (!ts) return "";
    const date = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts);
    return date.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const statusBadge = (status: string) => {
    const map: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
      open: { icon: <Clock className="w-3 h-3" />, color: "bg-amber-500/15 text-amber-400 border-amber-500/25", label: "Open" },
      resolved: { icon: <CheckCircle className="w-3 h-3" />, color: "bg-emerald-500/15 text-emerald-400 border-emerald-500/25", label: "Resolved" },
      closed: { icon: <XCircle className="w-3 h-3" />, color: "bg-gray-500/15 text-gray-400 border-gray-500/25", label: "Closed" },
    };
    const s = map[status] || map.open;
    return (
      <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border font-medium ${s.color}`}>
        {s.icon} {s.label}
      </span>
    );
  };

  // ──────────── NEW THREAD VIEW ────────────
  if (view === "new") {
    return (
      <div className="relative -m-4 sm:-m-6 md:-m-8 lg:-m-10 min-h-[calc(100vh-3.5rem)]">
        <AnimatedBackground />
        <div className="relative z-10 max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
          <button onClick={() => setView("threads")} className="flex items-center gap-1.5 text-gray-400 hover:text-white text-sm mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Threads
          </button>
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Plus className="w-6 h-6 text-emerald-400" />
              New Support Thread
            </h1>
            <p className="text-gray-400 text-sm mt-1">Describe your issue and our team will respond.</p>
          </div>
          <form onSubmit={handleCreateThread} className="space-y-5">
            <div className="bg-[#0F1419]/80 backdrop-blur-sm border border-gray-800/50 rounded-2xl p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Category</label>
                <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full bg-[#1A1F2B] border border-gray-700/50 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-emerald-500/50">
                  {categories.map((c) => (<option key={c} value={c}>{c}</option>))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Subject</label>
                <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Brief description of your issue" className="w-full bg-[#1A1F2B] border border-gray-700/50 rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500/50" maxLength={200} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Message</label>
                <textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Describe your issue in detail..." rows={5} className="w-full bg-[#1A1F2B] border border-gray-700/50 rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500/50 resize-none" maxLength={2000} />
                <p className="text-xs text-gray-600 mt-1 text-right">{message.length}/2000</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Attach Image / GIF (optional, max 3MB)</label>
                <div className="flex items-center gap-3">
                  <button type="button" onClick={() => fileInputRef.current?.click()} className="flex items-center gap-2 px-4 py-2 bg-gray-800/50 border border-gray-700/50 rounded-xl text-sm text-gray-300 hover:bg-gray-700/50 transition-colors">
                    <ImageIcon className="w-4 h-4" />
                    {imageFile ? "Change File" : "Choose File"}
                  </button>
                  {imageFile && (
                    <button type="button" onClick={() => { setImageFile(null); setImagePreview(null); }} className="text-red-400 hover:text-red-300 text-sm">Remove</button>
                  )}
                  <input type="file" ref={fileInputRef} accept="image/png,image/jpeg,image/jpg,image/gif,image/webp" onChange={(e) => handleImageSelect(e, "new")} className="hidden" />
                </div>
                {imagePreview && (
                  <div className="mt-3 relative inline-block">
                    <img src={imagePreview} alt="Preview" className="max-h-40 rounded-xl border border-gray-700/50" />
                    <button type="button" onClick={() => { setImageFile(null); setImagePreview(null); }} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
              <div className="bg-gray-800/30 rounded-xl p-4 space-y-1.5">
                <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold mb-2">Your Info</p>
                <p className="text-sm text-gray-300"><span className="text-gray-500">Email:</span> {user?.email || "Not available"}</p>
                <p className="text-sm text-gray-300"><span className="text-gray-500">Username:</span> {user?.username ? `${user.username}` : "Not set"}</p>
              </div>
            </div>
            <button type="submit" disabled={creating || !subject.trim() || !message.trim()} className="w-full py-3 px-4 bg-gradient-to-r from-[#61CE70] to-[#50B060] text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-emerald-500/20 transition-all text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {creating ? "Creating..." : "Create Thread"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ──────────── CHAT VIEW ────────────
  if (view === "chat" && selectedThread) {
    return (
      <div className="relative -m-4 sm:-m-6 md:-m-8 lg:-m-10 h-[calc(100vh-3.5rem)] flex flex-col">
        <AnimatedBackground />

        {/* Chat Header */}
        <div className="relative z-10 px-3 sm:px-5 pt-3 sm:pt-4 shrink-0">
          <div className="bg-[#0F1419]/90 backdrop-blur-sm border border-gray-800/50 rounded-2xl p-3 sm:p-4">
            <div className="flex items-center gap-3">
              <button onClick={() => { setView("threads"); setSelectedThread(null); setShowEmojiPicker(false); }} className="p-2 rounded-xl bg-gray-800/60 hover:bg-gray-700/60 transition-colors shrink-0">
                <ArrowLeft className="w-4 h-4" />
              </button>
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center text-white font-bold text-sm shrink-0 shadow-lg shadow-emerald-500/20">
                CT
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h2 className="text-white font-semibold text-sm truncate">Support@CashTap</h2>
                  <span className="w-2 h-2 bg-emerald-400 rounded-full shrink-0 animate-pulse" />
                </div>
                <p className="text-gray-500 text-xs truncate mt-0.5">{selectedThread.subject}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {statusBadge(selectedThread.status)}
                <span className="text-[10px] bg-gray-800/80 text-gray-400 px-2 py-0.5 rounded-full hidden sm:inline">{selectedThread.category}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Messages Area */}
        <div className="relative z-10 flex-1 overflow-y-auto px-3 sm:px-5 pb-2" style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
          <style>{`div::-webkit-scrollbar { display: none; }`}</style>

          {messages.length > 0 && (
            <div className="flex items-center gap-3 py-3">
              <div className="flex-1 h-px bg-gray-800/50" />
              <span className="text-[10px] text-gray-500 font-medium">
                {formatFullTime(messages[0]?.createdAt)}
              </span>
              <div className="flex-1 h-px bg-gray-800/50" />
            </div>
          )}

          {messages.map((msg) => {
            const isUser = msg.sender === "user";
            return (
              <div key={msg.id} className={`flex items-end gap-2 mt-3 ${isUser ? "flex-row-reverse" : ""}`}>
                <div className="w-7 h-7 shrink-0">
                  {isUser ? (
                    <div className="w-full h-full rounded-full bg-gradient-to-br from-[#61CE70] to-[#50B060] flex items-center justify-center text-white font-bold text-[10px] shadow-md shadow-emerald-500/20">
                      {(user?.username || user?.email || "U")[0].toUpperCase()}
                    </div>
                  ) : (
                    <div className="w-full h-full rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center text-white font-bold text-[10px] shadow-md shadow-emerald-500/20">
                      CT
                    </div>
                  )}
                </div>

                <div className="max-w-[75%] group">
                  <p className={`text-[10px] font-medium mb-1 px-1 ${isUser ? "text-right text-emerald-400/70" : "text-blue-400/70"}`}>
                    {isUser ? "You" : "Support@CashTap"}
                  </p>
                  <div className={`relative px-3.5 py-2.5 rounded-2xl backdrop-blur-sm ${
                    isUser
                      ? "bg-gradient-to-br from-[#61CE70]/20 to-[#50B060]/10 border border-[#61CE70]/20 rounded-br-md"
                      : "bg-[#1A1F2B]/90 border border-gray-700/30 rounded-bl-md"
                  }`}>
                    {msg.imageURL && (
                      <div className="relative mb-2">
                        <div className="absolute inset-0 flex items-center justify-center bg-gray-800/40 rounded-xl">
                          <Loader2 className="w-5 h-5 text-gray-300 animate-spin" />
                        </div>
                        <img
                          src={msg.imageURL} alt="Attachment"
                          className="max-h-52 max-w-full rounded-xl cursor-pointer hover:opacity-90 transition-opacity relative z-10"
                          onClick={() => window.open(msg.imageURL, "_blank")}
                          onLoad={(e) => { (e.target as HTMLImageElement).previousElementSibling?.classList.add("hidden"); }}
                        />
                      </div>
                    )}
                    {msg.message && msg.message !== "[Image]" && (
                      <p className="text-[13px] text-gray-200 whitespace-pre-wrap break-words leading-relaxed">{msg.message}</p>
                    )}
                    <span className={`text-[9px] text-gray-500 mt-1 block ${isUser ? "text-right" : ""}`}>
                      {formatTime(msg.createdAt)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>

        {/* Bottom input area - pinned */}
        <div className="relative z-10 shrink-0 px-3 sm:px-5 pb-3 sm:pb-4">
          {/* Chat image preview */}
          {chatImagePreview && (
            <div className="pb-2">
              <div className="relative inline-block bg-[#1A1F2B] rounded-xl p-2 border border-gray-700/30">
                <img src={chatImagePreview} alt="Preview" className="max-h-24 rounded-lg" />
                <button onClick={() => { setChatImageFile(null); setChatImagePreview(null); }} className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 transition-colors shadow-lg">
                  <X className="w-3 h-3" />
                </button>
              </div>
            </div>
          )}

          {selectedThread.status !== "closed" ? (
            <div className="relative">
              {showEmojiPicker && (
                <div ref={emojiPickerRef} className="absolute bottom-full mb-2 left-0 z-50">
                  <EmojiPicker
                    onEmojiClick={onEmojiClick}
                    theme={Theme.DARK}
                    width={320}
                    height={380}
                    searchPlaceHolder="Search emoji..."
                    lazyLoadEmojis
                  />
                </div>
              )}
              <div className="bg-[#0F1419]/90 backdrop-blur-sm border border-gray-800/50 rounded-2xl overflow-hidden">
                <div className="flex items-end gap-1 p-2">
                  <button onClick={() => setShowEmojiPicker(!showEmojiPicker)} className={`p-2 rounded-xl transition-colors shrink-0 ${showEmojiPicker ? "bg-emerald-500/20 text-emerald-400" : "hover:bg-gray-800/60 text-gray-400 hover:text-gray-200"}`} title="Emoji">
                    <Smile className="w-5 h-5" />
                  </button>
                  <button onClick={() => chatFileInputRef.current?.click()} className="p-2 rounded-xl hover:bg-gray-800/60 text-gray-400 hover:text-gray-200 transition-colors shrink-0" title="Attach image / GIF (max 3MB)">
                    <FileImage className="w-5 h-5" />
                  </button>
                  <input type="file" ref={chatFileInputRef} accept="image/png,image/jpeg,image/jpg,image/gif,image/webp" onChange={(e) => handleImageSelect(e, "chat")} className="hidden" />
                  <textarea
                    ref={textareaRef}
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSendMessage(); }
                    }}
                    placeholder="Type a message..."
                    rows={1}
                    className="flex-1 bg-transparent text-sm text-white placeholder-gray-500 focus:outline-none resize-none py-2 px-2 max-h-[120px] leading-relaxed"
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={sendingMessage || (!newMessage.trim() && !chatImageFile)}
                    className="p-2.5 rounded-xl bg-gradient-to-r from-[#61CE70] to-[#50B060] hover:shadow-lg hover:shadow-emerald-500/20 text-white transition-all disabled:opacity-30 disabled:cursor-not-allowed disabled:shadow-none shrink-0"
                  >
                    {sendingMessage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-3 text-gray-500 text-sm bg-[#0F1419]/90 backdrop-blur-sm border border-gray-800/50 rounded-xl">
              This thread has been closed.
            </div>
          )}
        </div>
      </div>
    );
  }

  // ──────────── THREAD LIST VIEW ────────────
  return (
    <div className="relative -m-4 sm:-m-6 md:-m-8 lg:-m-10 min-h-[calc(100vh-3.5rem)]">
      <AnimatedBackground />

      <div className="relative z-10 px-4 sm:px-6 md:px-8 lg:px-10 py-6 sm:py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3">
              <div className="p-2.5 bg-emerald-500/10 border border-emerald-500/20 rounded-2xl">
                <HeadphonesIcon className="w-7 h-7 text-emerald-400" />
              </div>
              Support
            </h1>
            <p className="text-gray-400 text-sm mt-2 ml-[52px]">{threads.length} {threads.length === 1 ? "thread" : "threads"}</p>
          </div>
          <button onClick={() => setView("new")} className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#61CE70] to-[#50B060] text-white font-semibold rounded-xl text-sm hover:shadow-lg hover:shadow-emerald-500/20 transition-all">
            <Plus className="w-4 h-4" /> New Thread
          </button>
        </div>

        {loadingThreads ? (
          <div className="flex items-center justify-center h-40">
            <Loader2 className="w-6 h-6 animate-spin text-emerald-400" />
          </div>
        ) : threads.length === 0 ? (
          <div className="text-center py-20 bg-[#0F1419]/60 backdrop-blur-sm border border-gray-800/50 rounded-2xl">
            <div className="w-20 h-20 rounded-full bg-gray-800/50 flex items-center justify-center mx-auto mb-5">
              <MessageSquare className="w-10 h-10 text-gray-600" />
            </div>
            <p className="text-gray-300 font-medium text-lg">No support threads yet</p>
            <p className="text-gray-500 text-sm mt-2 max-w-sm mx-auto">Create a new thread to get help from our support team. We're here 24/7.</p>
            <button onClick={() => setView("new")} className="mt-6 inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-[#61CE70] to-[#50B060] text-white font-semibold rounded-xl text-sm hover:shadow-lg hover:shadow-emerald-500/20 transition-all">
              <Plus className="w-4 h-4" /> Start a Conversation
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {threads.map((thread) => (
              <button
                key={thread.id}
                onClick={() => { setSelectedThread(thread); setView("chat"); }}
                className="w-full text-left bg-[#0F1419]/70 backdrop-blur-sm border border-gray-800/50 hover:border-emerald-500/20 rounded-2xl p-4 sm:p-5 transition-all group hover:shadow-lg hover:shadow-emerald-500/5"
              >
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center text-white font-bold text-sm shrink-0 shadow-lg shadow-emerald-500/20">
                    CT
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="text-white font-medium text-sm truncate group-hover:text-emerald-300 transition-colors">
                        {thread.subject}
                      </h3>
                      <span className="text-[10px] text-gray-500 shrink-0">{formatTime(thread.lastMessageAt)}</span>
                    </div>
                    <p className="text-gray-500 text-xs truncate mt-1">{thread.lastMessage}</p>
                    <div className="flex items-center justify-between gap-2 mt-2">
                      <span className="text-[10px] bg-gray-800/80 text-gray-400 px-2 py-0.5 rounded-full">{thread.category}</span>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {statusBadge(thread.status)}
                        {thread.unreadByUser > 0 && (
                          <span className="min-w-[18px] h-[18px] flex items-center justify-center bg-emerald-500 text-white text-[10px] font-bold rounded-full px-1 animate-pulse">
                            {thread.unreadByUser}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
